import { useState, Fragment, useEffect, useMemo } from 'react';

import { getTutorials } from '../../../../store/tutorials';
import { useAppSelector } from '../../../../store/configureStore';

import { tutorialConstants } from '../../../../utils/constants';
import { MUTUTInspectionItem, MUTUTInspectionOverviewData, PDFImageProps, VTCoverData, VTEquipmentAnomalyData } from '../../../../utils/models';

import Button from '../../../themed/button';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETAnomalySummary from '../et/etAnomalySummary';
import Modal from '../../../themed/modal';
import MUTCoverForm from '../../../forms/mut/mutCoverForm';
import MUTIsoPageForm from '../../../forms/mut/mutIsoPageForm';
import MUTPhotograpicRecord from '../../../forms/mut/mutPhotographicRecord';
import MUTReport from '.';
import UTInspectionOverviewForm from '../../../forms/mut/utInspectionOverviewForm';
import UTInspectionDataForm from '../../../forms/mut/utInspectionDataForm';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreateMUTReport = () => {
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [mutCoverData, setMUTCoverData] = useState<VTCoverData | null>();
	const [isoImage, setIsoImage] = useState<PDFImageProps[]>([]);
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [utInspectionOverview, setUTInspectionOverview] = useState<MUTUTInspectionOverviewData | null>();
	const [utInspectionItems, setUTInspectionItems] = useState<MUTUTInspectionItem[]>([]);

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = useMemo(() => {
		return {
			anomalyData,
			projectDetails,
			coverSheetData: mutCoverData,
			isoImage,
			photographicData,
			inspectionOverview: utInspectionOverview,
			inspectionItems: utInspectionItems,
		};
	}, [anomalyData, projectDetails, mutCoverData, isoImage, photographicData, utInspectionOverview, utInspectionItems]);

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setMUTCoverData(cachedReport.coverSheetData);
			setIsoImage(cachedReport.isoImage);
			setUTInspectionOverview(cachedReport.inspectionOverview);
			setUTInspectionItems(cachedReport.inspectionItems);
			setPhotographicData(cachedReport.photographicData);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<MUTReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!mutCoverData}>
				<MUTCoverForm onProceed={setMUTCoverData} />
			</Zippy>

			<Zippy headerTitle="ISO Page" isValid={isoImage.length > 0}>
				<MUTIsoPageForm onProceed={setIsoImage} />
			</Zippy>

			<Zippy
				headerTitle="UT Inspection Overview"
				isValid={!!utInspectionOverview}
			>
				<UTInspectionOverviewForm onProceed={setUTInspectionOverview} />
			</Zippy>

			<Zippy
				headerTitle="UT Inspection Items"
				isValid={utInspectionItems.length > 0}
			>
				<UTInspectionDataForm onProceed={setUTInspectionItems} />
			</Zippy>

			<Zippy headerTitle="Anomaly Data" isValid={anomalyData.length > 0}>
				<ETAnomalySummary
					anomalyData={anomalyData}
					onDataChange={setAnomalyData}
				/>
			</Zippy>

			<Zippy
				headerTitle="Photographic Report"
				isValid={photographicData.length > 0}
			>
				<MUTPhotograpicRecord onProceed={setPhotographicData} />
			</Zippy>

			<div className="d-flex justify-content-end">
				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreateMUTReport;
